/**
 * ReportModal Component
 *
 * Modal for configuring and running a report.
 * Includes date range selection, run button, and export options.
 */

import React, { useState } from 'react';
import { XMarkIcon, PlayIcon, DocumentChartBarIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { ReportDefinition, ReportDepartmentKey } from '../../types/reports';
import { ReportDateRangeSelector, getDefaultStartDate, getDefaultEndDate } from './ReportDateRangeSelector';
import { ReportExportButtons } from './ReportExportButtons';
import { getReportDepartmentLabel } from './ReportDepartmentTabs';
import { printCurrentPage, exportReportToCSV } from '../../utils/reportExport';
import { runReport, RunReportResponse } from '../../services/api';
import { getReportRenderer } from './renderers';

class ReportErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: string }
> {
  state = { hasError: false, error: '' };
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error: error.message };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700 font-medium">Report rendering failed</p>
          <p className="text-red-600 text-sm mt-1">{this.state.error}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

interface ReportModalProps {
  report: ReportDefinition;
  isOpen: boolean;
  onClose: () => void;
  locationId: number;
  locationName: string;
  department: ReportDepartmentKey;
}

type ReportStatus = 'idle' | 'running' | 'complete' | 'error';

export const ReportModal: React.FC<ReportModalProps> = ({
  report,
  isOpen,
  onClose,
  locationId,
  locationName,
  department,
}) => {
  // Date range state — single-date reports default to today, range reports to 1st of month
  const getInitialDate = () => {
    if (report.singleDate) {
      const now = new Date();
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    }
    return getDefaultStartDate();
  };
  const [startDate, setStartDate] = useState(getInitialDate);
  const [endDate, setEndDate] = useState(getDefaultEndDate);

  // Report execution state
  const [status, setStatus] = useState<ReportStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<RunReportResponse | null>(null);
  const [runningMessage, setRunningMessage] = useState('Generating report...');

  // Validate date range
  const isValidDateRange = startDate && endDate && startDate <= endDate;

  // Check if an error is a timeout/network error worth retrying
  const isRetryableError = (err: unknown): boolean => {
    const msg = err instanceof Error ? err.message.toLowerCase() : '';
    return msg.includes('failed to fetch') || msg.includes('networkerror') ||
      msg.includes('timeout') || msg.includes('504') || msg.includes('503') ||
      msg.includes('network') || msg.includes('aborted') || msg.includes('timed out');
  };

  // Handle running the report (with auto-retry on timeout)
  const handleRunReport = async () => {
    if (!isValidDateRange) return;

    setStatus('running');
    setError(null);
    setReportData(null);
    setRunningMessage('Generating report...');

    // Show "warming up" message after 5 seconds
    const warmupTimer = setTimeout(() => {
      setRunningMessage('Warming up connection...');
    }, 5000);

    const MAX_ATTEMPTS = 2;
    let lastError: unknown;

    try {
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          const response = await runReport(report.id, locationId, startDate, endDate);

          if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to generate report');
          }

          clearTimeout(warmupTimer);
          setReportData(response.data);
          setStatus('complete');
          return;
        } catch (err) {
          lastError = err;
          if (attempt < MAX_ATTEMPTS && isRetryableError(err)) {
            console.warn(`Report attempt ${attempt} failed, retrying...`, err);
            setRunningMessage('Retrying...');
            continue;
          }
          throw err;
        }
      }
    } catch (err) {
      clearTimeout(warmupTimer);
      setError(lastError instanceof Error ? lastError.message : 'Failed to generate report');
      setStatus('error');
    }
  };

  // Handle print to PDF
  const handlePrintPDF = () => {
    printCurrentPage();
  };

  // Handle export to CSV
  const handleExportExcel = () => {
    if (!reportData?.data) return;

    const data = reportData.data as any;

    // Build rows for CSV from report data
    const rows: Record<string, unknown>[] = [];
    const deptLabels: Record<string, string> = {
      NEW_VEHICLE: 'New', USED_VEHICLE: 'Used', SERVICE: 'Service',
      PARTS: 'Parts', BODY_SHOP: 'Body', RENTAL: 'Rental', OTHERS: 'Others',
    };
    const deptOrder = ['NEW_VEHICLE', 'USED_VEHICLE', 'SERVICE', 'PARTS', 'BODY_SHOP', 'RENTAL', 'OTHERS'];

    if (data.yesterdayGross !== undefined) {
      // CEO Daily Briefing format — flatten all 6 sections
      rows.push({ description: '=== 1. YESTERDAY\'S GROSS ===' });
      for (const loc of data.yesterdayGross) {
        const r: Record<string, unknown> = { description: loc.locationName };
        for (const d of deptOrder) r[deptLabels[d]] = loc.departments[d] || 0;
        r['Total'] = loc.total;
        rows.push(r);
      }
      if (data.yesterdayCompanyTotal) {
        const ct = data.yesterdayCompanyTotal;
        const r: Record<string, unknown> = { description: 'Company Total' };
        for (const d of deptOrder) r[deptLabels[d]] = ct.departments[d] || 0;
        r['Total'] = ct.total;
        rows.push(r);
      }
      rows.push({ description: '' });

      rows.push({ description: '=== 2. UNITS SOLD YESTERDAY ===' });
      for (const loc of data.unitsSoldYesterday) {
        rows.push({ description: loc.locationName, New: loc.newUnits, Used: loc.usedUnits, Total: loc.totalUnits });
      }
      if (data.unitsCompanyTotal) {
        rows.push({ description: 'Company Total', New: data.unitsCompanyTotal.newUnits, Used: data.unitsCompanyTotal.usedUnits, Total: data.unitsCompanyTotal.totalUnits });
      }
      rows.push({ description: '' });

      rows.push({ description: '=== 3. MTD TOTALS ===' });
      for (let i = 0; i < data.mtdGross.length; i++) {
        const loc = data.mtdGross[i];
        const units = data.mtdUnits?.[i];
        const r: Record<string, unknown> = { description: loc.locationName };
        for (const d of deptOrder) r[deptLabels[d]] = loc.departments[d] || 0;
        r['Total'] = loc.total;
        r['New U'] = units?.newUnits || 0;
        r['Used U'] = units?.usedUnits || 0;
        rows.push(r);
      }
      rows.push({ description: '' });

      rows.push({ description: '=== 4. FORECAST VARIANCE ===' });
      for (const fv of (data.forecastVariance || [])) {
        rows.push({
          description: fv.locationName,
          Department: deptLabels[fv.department] || fv.department,
          Trending: fv.trending,
          Target: fv.projectionTarget,
          Gap: fv.gap,
          'Gap %': `${fv.gapPercent}%`,
        });
      }
      rows.push({ description: '' });

      rows.push({ description: '=== 5. OPEN ROs ===' });
      if (data.openROs?.length > 0) {
        let totalClosed = 0, totalOpen = 0, totalOver120 = 0;
        for (const loc of data.openROs) {
          rows.push({
            description: loc.locationName,
            'Closed ROs': loc.closedROs,
            'Open ROs': loc.openROs,
            '120+ Days': loc.over120,
          });
          totalClosed += loc.closedROs;
          totalOpen += loc.openROs;
          totalOver120 += loc.over120;
          for (const a of loc.advisors) {
            rows.push({
              description: `  ${a.advisorName}`,
              'Closed ROs': a.closedROs,
              'Open ROs': a.openROs,
              '120+ Days': a.over120,
            });
          }
        }
        rows.push({
          description: 'Company Total',
          'Closed ROs': totalClosed,
          'Open ROs': totalOpen,
          '120+ Days': totalOver120,
        });
      }
      rows.push({ description: '' });

      rows.push({ description: '=== 6. LOW FRH FLAGS ===' });
      if (data.lowFRHFlags?.length > 0) {
        for (const f of data.lowFRHFlags) {
          rows.push({ description: f.techName, Location: f.locationName, 'Actual FRH': f.actualFRH, 'Expected FRH': f.expectedFRH, '% of Expected': `${f.percentOfExpected}%` });
        }
      } else {
        rows.push({ description: 'No technicians flagged' });
      }

      // Use simple two-column format for CSV export
      const columns = [
        { key: 'description', header: 'Description' },
        ...deptOrder.map(d => ({ key: deptLabels[d], header: deptLabels[d] })),
        { key: 'Total', header: 'Total' },
        { key: 'New', header: 'New' },
        { key: 'Used', header: 'Used' },
        { key: 'New U', header: 'New U' },
        { key: 'Used U', header: 'Used U' },
        { key: 'Department', header: 'Department' },
        { key: 'Trending', header: 'Trending' },
        { key: 'Target', header: 'Target' },
        { key: 'Gap', header: 'Gap' },
        { key: 'Gap %', header: 'Gap %' },
        { key: 'Location', header: 'Location' },
        { key: 'Total Open', header: 'Total Open' },
        { key: '120+ Days', header: '120+ Days' },
        { key: 'Actual FRH', header: 'Actual FRH' },
        { key: 'Expected FRH', header: 'Expected FRH' },
        { key: '% of Expected', header: '% of Expected' },
      ];

      exportReportToCSV(report, { locationId, locationName, department, startDate, endDate }, rows, columns);
      return;
    } else if (data.partsSales !== undefined) {
      // Parts Selling Gross format
      rows.push({ description: 'Parts Sales (Revenue)', amount: data.partsSales });
      rows.push({ description: 'Parts Cost of Sales', amount: data.partsCOS });
      rows.push({ description: 'PARTS GROSS', amount: data.partsGross });
      rows.push({ description: '', amount: '' });
      // Pay type breakdown
      if (data.grossByPayType && data.grossByPayType.length > 0) {
        rows.push({ description: 'GROSS BY PAY TYPE', amount: '', sales: '', cos: '', gp: '' });
        for (const pt of data.grossByPayType) {
          const gp = pt.sales !== 0 ? `${((pt.gross / pt.sales) * 100).toFixed(1)}%` : '—';
          rows.push({
            description: `  ${pt.label}`,
            amount: pt.gross,
            sales: pt.sales,
            cos: pt.cos,
            gp,
          });
        }
        rows.push({ description: '', amount: '' });
      }
      if (data.salesExpenses) {
        for (const exp of data.salesExpenses) {
          rows.push({ description: `${exp.accountCode} - ${exp.description}`, amount: exp.amount });
        }
      }
      rows.push({ description: 'TOTAL SALES EXPENSES', amount: data.totalSalesExpenses });
      rows.push({ description: '', amount: '' });
      rows.push({ description: 'PARTS DEPT SELLING GROSS', amount: data.sellingGross });
    } else if (data.serviceSales !== undefined) {
      // Service Selling Gross format
      rows.push({ description: 'Service Sales (Revenue)', amount: data.serviceSales });
      rows.push({ description: 'Service Cost of Sales', amount: data.serviceCOS });
      rows.push({ description: 'SERVICE GROSS', amount: data.serviceGross });
      rows.push({ description: '', amount: '' });
      if (data.grossByPayType && data.grossByPayType.length > 0) {
        rows.push({ description: 'GROSS BY PAY TYPE', amount: '', sales: '', cos: '', gp: '' });
        for (const pt of data.grossByPayType) {
          const gp = pt.sales !== 0 ? `${((pt.gross / pt.sales) * 100).toFixed(1)}%` : '—';
          rows.push({
            description: `  ${pt.label}`,
            amount: pt.gross,
            sales: pt.sales,
            cos: pt.cos,
            gp,
          });
        }
        rows.push({ description: '', amount: '' });
      }
      if (data.salesExpenses) {
        for (const exp of data.salesExpenses) {
          rows.push({ description: `${exp.accountCode} - ${exp.description}`, amount: exp.amount });
        }
      }
      rows.push({ description: 'TOTAL SALES EXPENSES', amount: data.totalSalesExpenses });
      rows.push({ description: '', amount: '' });
      rows.push({ description: 'SERVICE DEPT SELLING GROSS', amount: data.sellingGross });
    } else if (data.newVehicleSales !== undefined) {
      // New Selling Gross format
      rows.push({ description: 'New Vehicle Sales (Revenue)', amount: data.newVehicleSales });
      rows.push({ description: 'New Vehicle Cost of Sales', amount: data.newVehicleCOS });
      rows.push({ description: 'NEW VEHICLE GROSS', amount: data.newVehicleGross });
      rows.push({ description: '', amount: '' });
      rows.push({ description: 'F&I GROSS (NEW)', amount: data.fiGross });
      rows.push({ description: '', amount: '' });
      rows.push({ description: 'TOTAL GROSS', amount: data.totalGross });
      rows.push({ description: '', amount: '' });
      rows.push({ description: 'VARIABLE EXPENSES', amount: '' });
      if (data.variableExpenses) {
        for (const exp of data.variableExpenses) {
          rows.push({ description: `  ${exp.accountCode} - ${exp.description}`, amount: exp.amount });
        }
      }
      rows.push({ description: 'TOTAL VARIABLE EXPENSES', amount: data.totalVariableExpenses });
      rows.push({ description: '', amount: '' });
      rows.push({ description: 'SEMI-FIXED EXPENSES', amount: '' });
      if (data.semiFixedExpenses) {
        for (const exp of data.semiFixedExpenses) {
          rows.push({ description: `  ${exp.accountCode} - ${exp.description}`, amount: exp.amount });
        }
      }
      rows.push({ description: 'TOTAL SEMI-FIXED EXPENSES', amount: data.totalSemiFixedExpenses });
      rows.push({ description: '', amount: '' });
      rows.push({ description: 'NEW VEHICLE DEPT SELLING GROSS', amount: data.sellingGross });
    } else if (data.usedVehicleSales !== undefined) {
      // Used Selling Gross format
      rows.push({ description: 'Used Vehicle Sales (Revenue)', amount: data.usedVehicleSales });
      rows.push({ description: 'Used Vehicle Cost of Sales', amount: data.usedVehicleCOS });
      rows.push({ description: 'USED VEHICLE GROSS', amount: data.usedVehicleGross });
      rows.push({ description: '', amount: '' });
      rows.push({ description: 'F&I GROSS (USED)', amount: data.fiGross });
      rows.push({ description: '', amount: '' });
      rows.push({ description: 'TOTAL GROSS', amount: data.totalGross });
      rows.push({ description: '', amount: '' });
      rows.push({ description: 'VARIABLE EXPENSES', amount: '' });
      if (data.variableExpenses) {
        for (const exp of data.variableExpenses) {
          rows.push({ description: `  ${exp.accountCode} - ${exp.description}`, amount: exp.amount });
        }
      }
      rows.push({ description: 'TOTAL VARIABLE EXPENSES', amount: data.totalVariableExpenses });
      rows.push({ description: '', amount: '' });
      rows.push({ description: 'SEMI-FIXED EXPENSES', amount: '' });
      if (data.semiFixedExpenses) {
        for (const exp of data.semiFixedExpenses) {
          rows.push({ description: `  ${exp.accountCode} - ${exp.description}`, amount: exp.amount });
        }
      }
      rows.push({ description: 'TOTAL SEMI-FIXED EXPENSES', amount: data.totalSemiFixedExpenses });
      rows.push({ description: '', amount: '' });
      rows.push({ description: 'USED VEHICLE DEPT SELLING GROSS', amount: data.sellingGross });
    } else if (data.bodySales !== undefined) {
      // Body Shop Selling Gross format (2-column: Description / Amount)
      rows.push({ description: 'Body Shop Sales (Revenue)', amount: data.bodySales });
      rows.push({ description: 'Body Shop Cost of Sales', amount: data.bodyCOS });
      rows.push({ description: 'BODY SHOP GROSS', amount: data.bodyGross });
      rows.push({ description: '', amount: '' });
      if (data.salesExpenses) {
        for (const exp of data.salesExpenses) {
          rows.push({ description: `${exp.accountCode} - ${exp.description}`, amount: exp.amount });
        }
      }
      rows.push({ description: 'TOTAL SALES EXPENSES', amount: data.totalSalesExpenses });
      rows.push({ description: '', amount: '' });
      rows.push({ description: 'BODY SHOP DEPT SELLING GROSS', amount: data.sellingGross });
    } else if (data.managers !== undefined && data.summary !== undefined) {
      // Sales Team Performance format (hierarchical manager → salesperson)
      for (const mgr of data.managers) {
        rows.push({
          description: `${mgr.managerName} (Sales Manager)`,
          units: mgr.totalUnits,
          newUsed: `${mgr.newUnits}N / ${mgr.usedUnits}U`,
          frontGross: mgr.frontGross,
          holdback: mgr.holdback,
          fiGross: mgr.fiGross,
          totalGross: mgr.totalGross,
          avgPerUnit: mgr.avgTotalGrossPerUnit ?? '',
        });
        if (mgr.salespeople) {
          for (const sp of mgr.salespeople) {
            rows.push({
              description: `  ${sp.salespersonName}`,
              units: sp.totalUnits,
              newUsed: `${sp.newUnits}N / ${sp.usedUnits}U`,
              frontGross: sp.frontGross,
              holdback: sp.holdback,
              fiGross: sp.fiGross,
              totalGross: sp.totalGross,
              avgPerUnit: sp.avgTotalGrossPerUnit ?? '',
            });
          }
        }
        rows.push({ description: '', units: '', newUsed: '', frontGross: '', holdback: '', fiGross: '', totalGross: '', avgPerUnit: '' });
      }
      rows.push({
        description: 'GRAND TOTAL',
        units: data.summary.totalUnits,
        newUsed: `${data.summary.newUnits}N / ${data.summary.usedUnits}U`,
        frontGross: data.summary.frontGross,
        holdback: data.summary.holdback,
        fiGross: data.summary.fiGross,
        totalGross: data.summary.totalGross,
        avgPerUnit: data.summary.avgTotalGrossPerUnit ?? '',
      });
    }

    // Determine columns based on report type
    const isSalesTeam = data.managers !== undefined && data.summary !== undefined;
    const hasPayTypes = (data.partsSales !== undefined || data.serviceSales !== undefined)
      && data.grossByPayType?.length > 0;
    const columns = isSalesTeam
      ? [
          { key: 'description', header: 'Name' },
          { key: 'units', header: 'Units' },
          { key: 'newUsed', header: 'N / U' },
          { key: 'frontGross', header: 'Front Gross' },
          { key: 'holdback', header: 'Holdback' },
          { key: 'fiGross', header: 'F&I' },
          { key: 'totalGross', header: 'Total Gross' },
          { key: 'avgPerUnit', header: 'Avg / Unit' },
        ]
      : hasPayTypes
      ? [
          { key: 'description', header: 'Description' },
          { key: 'sales', header: 'Sales' },
          { key: 'cos', header: 'COS' },
          { key: 'amount', header: 'Gross' },
          { key: 'gp', header: 'GP%' },
        ]
      : [
          { key: 'description', header: 'Description' },
          { key: 'amount', header: 'Amount' },
        ];

    exportReportToCSV(
      report,
      { locationId, locationName, department, startDate, endDate },
      rows,
      columns
    );
  };

  // Handle close and reset
  const handleClose = () => {
    setStatus('idle');
    setError(null);
    setReportData(null);
    setStartDate(getInitialDate());
    setEndDate(getDefaultEndDate());
    onClose();
  };

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  const departmentLabel = getReportDepartmentLabel(department);
  const hasResults = status === 'complete';
  const Renderer = getReportRenderer(report.id);

  return (
    <div
      className="fixed inset-0 z-50 overflow-hidden flex items-center justify-center p-4 print:p-0 print:overflow-visible print:static print:block"
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity print:hidden" />

      {/* Modal - Large size for data visualization */}
      <div className="relative max-w-6xl w-full max-h-[95vh] bg-white rounded-lg shadow-xl flex flex-col print:max-w-none print:max-h-none print:rounded-none print:shadow-none print:block print:overflow-visible">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between print:border-b-2">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{report.title}</h2>
            <p className="text-sm text-gray-500 mt-1">
              {locationName} • {departmentLabel}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 print:hidden"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 print:overflow-visible print:flex-none">
          {/* Date Selector */}
          <div className="mb-6 print:hidden">
            {report.singleDate ? (
              <>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Report Date</h3>
                <div className="max-w-xs">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <CalendarIcon className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => { setStartDate(e.target.value); setEndDate(e.target.value); }}
                      disabled={status === 'running'}
                      className={`block w-full pl-10 pr-3 py-2 text-sm border rounded-md shadow-sm
                        focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500
                        ${status === 'running' ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white text-gray-900'}
                        border-gray-300`}
                    />
                  </div>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Report Period</h3>
                <ReportDateRangeSelector
                  startDate={startDate}
                  endDate={endDate}
                  onStartDateChange={setStartDate}
                  onEndDateChange={setEndDate}
                  disabled={status === 'running'}
                />
              </>
            )}
          </div>

          {/* Run Report Button */}
          <div className="mb-6 print:hidden">
            <button
              onClick={handleRunReport}
              disabled={!isValidDateRange || status === 'running'}
              className={`
                inline-flex items-center px-4 py-2 text-sm font-medium rounded-md
                transition-colors
                ${!isValidDateRange || status === 'running'
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
                }
              `}
            >
              {status === 'running' ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4 mr-2"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  {runningMessage}
                </>
              ) : (
                <>
                  <PlayIcon className="h-4 w-4 mr-2" />
                  Run Report
                </>
              )}
            </button>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 mb-6 print:hidden" />

          {/* Results Area */}
          <div id="report-print-content">
            {/* Print Header (only visible when printing) */}
            <div className="hidden print:block mb-4">
              <h1 className="text-xl font-bold">{report.title}</h1>
              <p className="text-sm text-gray-600">
                {locationName} • {departmentLabel} • {report.singleDate ? startDate : `${startDate} to ${endDate}`}
              </p>
            </div>

            {status === 'idle' && (
              <div className="text-center py-12">
                <DocumentChartBarIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">
                  {report.singleDate
                    ? 'Select a date and click "Run Report" to generate results.'
                    : 'Select a date range and click "Run Report" to generate results.'}
                </p>
              </div>
            )}

            {status === 'running' && (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-blue-600 mb-4" />
                <p className="text-gray-500">{runningMessage}</p>
              </div>
            )}

            {status === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700">{error || 'An error occurred while generating the report.'}</p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
                >
                  Try again
                </button>
              </div>
            )}

            {status === 'complete' && reportData && Renderer && (
              <ReportErrorBoundary>
                <Renderer data={reportData.data} />
              </ReportErrorBoundary>
            )}

            {status === 'complete' && reportData && !Renderer && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-4">
                  <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h4 className="text-lg font-medium text-green-900 mb-2">Report Ready</h4>
                <p className="text-sm text-green-700">
                  Report generated for {startDate} to {endDate}.
                  Use the export buttons below to save this report.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between print:hidden">
          <ReportExportButtons
            onPrintPDF={handlePrintPDF}
            onExportExcel={handleExportExcel}
            disabled={!hasResults}
          />
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
